WHAT A STRONG ACTION NODE LOOKS LIKE

  Action nodes are the workhorse of a flow — and they are also the unit the
  cache stores and replays. Sizing them well is what makes a flow both fast
  (cacheable sooner) and durable (resistant to benign UI churn). Four things
  separate a strong action node from a weak one.

  1. IT READS AS ONE COHERENT STEP

    A good node has a title you could read aloud as a single instruction to a
    teammate: "Create a ticket", "Fill out the subscription form", "Click Start
    to open the run-config modal". The title names a goal — and, where it helps,
    the purpose of the step — not raw mechanics.

    A node is usually broader than a single click, but not always: a lone action
    that genuinely stands on its own in the flow is a fine node ("Click Start to
    open the modal"). The extremes to avoid are a purely mechanical title with no
    intent ("Click the button") and a node stuffed with several unrelated
    outcomes ("Create a ticket, assign it, comment, and upload"). When in doubt,
    err toward clearer, more discrete steps over one overloaded node.

  2. IT HAS ROUGHLY 3–8 PLAYBOOK STEPS

    Each playbook step is one atomic intent executed in one tool call, bounded
    by stable page states. Fuse actions coupled through ephemeral UI state — an
    opener and a target that exists only while its menu is open — into that
    call. Split unrelated intents and every stable intermediate. A healthy node
    lands around 3–8 steps:

      click → fill_form → click            (good — open, fill, submit)
      click → click → fill_form → click    (good)
      27 clicks                            (bad — that's several outcomes)

    "Meaningful" is the operative word. Incidental events collapse into the
    nearest step (a focus-click before a fill is part of the fill), and a whole
    form-fill is ONE fill_form step, not one step per field. If a single outcome
    would need more than ~8 steps, that's the signal to split it into sequential
    action nodes ("Fill customer details" → "Fill billing details" → "Review &
    submit") rather than grow one megastep.

    This ~3–8 is the STEP count inside one action node — not a cap on how many
    NODES a flow has. A flow is as long as its coherent journey needs (see Best
    Practices on flow scope).

    Interaction recipes are property-scoped, reusable interaction mechanics.
    A recipe invocation appears as one `call` step for its whole transaction,
    not as separate steps for the browser actions inside it.

  3. IT HAS A CLEAR ASSERTION BOUNDARY

    A strong node ends at a checkpoint you can assert on: a modal disappeared,
    the URL changed, a row appeared, a toast fired. Put that check inline on the
    node (see the Assertions section) so the node's success is observable and the
    run gets a real progress marker at each step.

    Inline assertions verify the END STATE after the action. They can be relative
    or temporal ("the row count dropped by 2"), but only if the baseline was
    captured upstream — write `current row count = $var.captured_row_count - 2`,
    never "the count dropped by 2". The reason: an action may replay from cache
    while its assertion is judged live by the agent, which does NOT have the
    action's execution context. A self-contained assertion (absolute values, or
    values bound to captured variables) survives that split; one that silently
    assumes "before" state does not.

  4. IT IS NOT CONDITIONAL

    Avoid baking branching into an action node — "if the location isn't New York,
    set it to New York", "if patient X isn't in the table, create patient X". A
    conditional in a node usually means the data wasn't staged properly, and it
    caches poorly (the cached path and the live path diverge).

    Prefer, in order:
      - Seed the precondition so the branch never exists ("patient X always
        present" → seed patient X). See Seed Workflows.
      - If the action is idempotent — the same sequence reaches the desired state
        regardless of where it started (re-selecting "New York" works whether or
        not it was already set) — just run the sequence unconditionally. No branch
        needed.
      - If the branch is genuine, use a formal CONDITION node (deterministic when
        the check is a $var / identity comparison; agentic otherwise), which keeps
        the branch explicit and cacheable per path.

    If a conditional truly must live inside a node, set `alwaysUseAgent: true` on
    it so the branch is reasoned about live every run and never mis-cached.

  WHY THIS MATTERS

    The node is the atomic cache unit. The smaller and more reliable each node is,
    the sooner the flow caches and the faster it replays. But because the node is
    also the unit of specificity, the more a node pins to incidental detail (exact
    button text, a precise field order), the more a benign change breaks it.
    Nailing the altitude — coherent goal, ~3–8 meaningful steps, a clean assertion
    boundary, no hidden branches — is what buys both speed and reliability at once.
